import { ChangeDetectionStrategy, Component, computed, inject, input, resource } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RegisterTabComponent } from '@core/decorators/register-tab-component.decorator';
import { ContractExpiryWarningDays } from '@features/organization/constants/contract-expiry.constant';
import { OrganizationTabKeys } from '@features/organization/constants/organization-tab-keys.constant';
import { TranslocoDirective } from '@jsverse/transloco';
import type { UUID } from '@kerberos-compliance/compliance-shared-ts/common/types/uuid.type';
import { isFeatureWorkItemType } from '@kerberos-compliance/compliance-shared-ts/features/work-item/utilities/work-item-type-sort.utilities';
import {
  ButtonComponent,
  ButtonVariantMap,
  IconComponent,
  IconNameMap,
  IconSizeMap,
  LoaderComponent,
} from '@kerberos-compliance/compliance-shared-ui';
import dayjs from 'dayjs';
import { NzCheckboxComponent } from 'ng-zorro-antd/checkbox';
import { NzCollapseModule } from 'ng-zorro-antd/collapse';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';
import { ContractsDetailFacade } from './facades/contracts-detail.facade';
import { ContractsService } from './services/contracts.service';
import { ContractsDetailLabelsService } from './services/contracts-detail-labels.service';
import { ContractsDetailStore } from './stores/contracts-detail.store';
import { ContractsDetailUiStore } from './stores/contracts-detail-ui.store';
import type { ResponseContractDetailsDto } from './types/contracts.types';

@RegisterTabComponent(OrganizationTabKeys.Contracts)
@Component({
  selector: 'aad-contracts-detail',
  imports: [
    FormsModule,
    NzCheckboxComponent,
    NzCollapseModule,
    NzTableModule,
    NzTagModule,
    NzTooltipModule,
    TranslocoDirective,
    LoaderComponent,
    IconComponent,
    ButtonComponent,
  ],
  providers: [
    ContractsDetailUiStore,
    ContractsService,
    ContractsDetailStore,
    ContractsDetailLabelsService,
    ContractsDetailFacade,
  ],
  templateUrl: 'contracts-detail.component.html',
  styleUrl: 'contracts-detail.component.less',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ContractsDetailComponent {
  protected readonly contractsDetailFacade = inject(ContractsDetailFacade);

  protected readonly labels = inject(ContractsDetailLabelsService);

  protected readonly ButtonVariantMap = ButtonVariantMap;

  protected readonly IconNameMap = IconNameMap;

  protected readonly IconSizeMap = IconSizeMap;

  protected readonly isFeatureWorkItemType = isFeatureWorkItemType;

  public readonly organizationId = input<UUID>();

  protected readonly contracts = this.contractsDetailFacade.contracts;

  /**
   * The facade's available work item types, sorted alphabetically by their rendered (locale-aware) label so
   * the list stays stable across reloads, reveal toggles and a language switch.
   */
  protected readonly availableWorkItemTypes = computed(() =>
    this.contractsDetailFacade
      .availableWorkItemTypes()
      .toSorted((first, second) =>
        this.labels.getWorkItemTypeLabel(first).localeCompare(this.labels.getWorkItemTypeLabel(second)),
      ),
  );

  protected readonly loading = this.contractsDetailFacade.loading;

  // noinspection JSUnusedGlobalSymbols - placeholder to keep the resource
  protected retrieveDataOnIdChange = resource({
    params: () => this.organizationId(),
    loader: ({ params }) => this.contractsDetailFacade.loadByOrganizationId(params),
  });

  /**
   * Returns the whole number of months remaining until the contract ends (never negative). Only meaningful for
   * an active contract, whose end date is today or later; inactive contracts show their state instead, since
   * the clamp here would otherwise report a stale contract as having "0 months left".
   *
   * @param {ResponseContractDetailsDto} contract - The contract.
   * @returns {number} The number of full months left.
   */
  protected getMonthsLeft(contract: ResponseContractDetailsDto): number {
    return Math.max(0, dayjs(contract.endDate).diff(dayjs(), 'month'));
  }

  /**
   * Returns the whole number of calendar days remaining until the contract ends (never negative).
   *
   * Both sides are floored to the start of the day so the count reflects calendar days and stays
   * stable throughout the day, rather than drifting with the wall-clock time of the request.
   *
   * @param {ResponseContractDetailsDto} contract - The contract.
   * @returns {number} The number of full days left.
   */
  protected getDaysLeft(contract: ResponseContractDetailsDto): number {
    return Math.max(0, dayjs(contract.endDate).startOf('day').diff(dayjs().startOf('day'), 'day'));
  }

  /**
   * Determines whether the contract's end date falls within the "expiring soon" warning threshold.
   *
   * @param {ResponseContractDetailsDto} contract - The contract.
   * @returns {boolean} True when the contract has an end date within {@link ContractExpiryWarningDays} days.
   */
  protected isExpiringSoon(contract: ResponseContractDetailsDto): boolean {
    if (!contract.endDate) {
      return false;
    }
    return this.getDaysLeft(contract) <= ContractExpiryWarningDays;
  }
}
